import { useState, useCallback, useEffect } from 'react'
import { Avatar, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button, FormControl, InputLabel, Select, TextField, Box, Typography } from '@mui/material'
import { Lock, Logout, Person, Palette, HelpOutlined, DescriptionOutlined } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import type { AuthUser } from '@crm/shared'
import { fetchPreferences, updatePreferences, type UserPreferences } from '../../api/preferences'

interface HeaderUserMenuProps {
  user: AuthUser | null
  onChangePassword: () => void
  onLogout: () => void
}

export function HeaderUserMenu({ user, onChangePassword, onLogout }: HeaderUserMenuProps) {
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [prefOpen, setPrefOpen] = useState(false)
  const [prefs, setPrefs] = useState<UserPreferences>({ theme: 'system', background_url: null })
  const [bgUrl, setBgUrl] = useState('')

  const loadPrefs = useCallback(async () => {
    try {
      const result = await fetchPreferences()
      setPrefs(result.data ?? { theme: 'system', background_url: null })
      setBgUrl(result.data?.background_url ?? '')
    } catch { /* ignore */ }
  }, [])

  useEffect(() => {
    if (prefOpen) loadPrefs()
  }, [prefOpen, loadPrefs])

  const handleSavePrefs = useCallback(async () => {
    try {
      await updatePreferences({ theme: prefs.theme, background_url: bgUrl || null })
      setPrefOpen(false)
    } catch { /* ignore */ }
  }, [prefs.theme, bgUrl])

  const applyTheme = useCallback((theme: string) => {
    const body = document.documentElement
    if (theme === 'dark') {
      body.setAttribute('data-mui-color-scheme', 'dark')
    } else if (theme === 'light') {
      body.setAttribute('data-mui-color-scheme', 'light')
    } else {
      body.removeAttribute('data-mui-color-scheme')
    }
  }, [])

  const handleThemeChange = useCallback((theme: string) => {
    setPrefs((p) => ({ ...p, theme }))
    applyTheme(theme)
  }, [applyTheme])

  if (!user) {
    return null
  }

  return (
    <>
      <Tooltip title={user.display_name || user.username}>
        <IconButton onClick={(event) => setAnchorEl(event.currentTarget)} size="small" sx={{ ml: 1 }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14 }}>
            {(user.display_name || user.username).charAt(0).toUpperCase()}
          </Avatar>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem disabled>
          <ListItemIcon><Person fontSize="small" /></ListItemIcon>
          <ListItemText primary={user.display_name || user.username} secondary={user.email} />
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { setAnchorEl(null); setPrefOpen(true) }}>
          <ListItemIcon><Palette fontSize="small" /></ListItemIcon>
          <ListItemText primary="Preferences" />
        </MenuItem>
        <MenuItem onClick={() => { setAnchorEl(null); onChangePassword() }}>
          <ListItemIcon><Lock fontSize="small" /></ListItemIcon>
          <ListItemText primary="Change Password" />
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { setAnchorEl(null); navigate('/docs/help') }}>
          <ListItemIcon><HelpOutlined fontSize="small" /></ListItemIcon>
          <ListItemText primary="Help" />
        </MenuItem>
        <MenuItem onClick={() => { setAnchorEl(null); window.open('/docs/api', '_blank') }}>
          <ListItemIcon><DescriptionOutlined fontSize="small" /></ListItemIcon>
          <ListItemText primary="API Docs" />
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { setAnchorEl(null); onLogout() }}>
          <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
          <ListItemText primary="Sign Out" />
        </MenuItem>
      </Menu>

      <Dialog open={prefOpen} onClose={() => setPrefOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Preferences</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Theme</InputLabel>
              <Select value={prefs.theme} label="Theme" onChange={(e) => handleThemeChange(e.target.value)}>
                <MenuItem value="system">System</MenuItem>
                <MenuItem value="light">Light</MenuItem>
                <MenuItem value="dark">Dark</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Background Image URL"
              size="small"
              fullWidth
              value={bgUrl}
              onChange={(e) => setBgUrl(e.target.value)}
              placeholder="https://example.com/bg.jpg"
              helperText="Optional background image for the app"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrefOpen(false)}>Cancel</Button>
          <Button onClick={handleSavePrefs} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
